import { TAttendeeLimit, TMeetingRoom } from "apis/meetings/meeting-room";
import { IEditReservationModalMethods } from "./types";
import { extractHtmlText } from "utils/validation";
import {
  checkDuplicationRecurringReservation,
  TFormattedAttendee,
  TReservation,
  updateMeetingReservation,
} from "apis/meetings/meeting-reservation";
import { RepeatReservationConditionalOption } from "uis/modal/repeat-edit-conditinal-modal";

export const services: IEditReservationModalMethods = {
  checkChanges: (
    initData: any,
    selectedStartTime: Date,
    selectedEndTime: Date,
    selectedMeetingRoom: TMeetingRoom,
    title: string,
    isPrivate: boolean,
    guestsCanModify: boolean,
    isChangedAttendees: boolean,
    memo?: string
  ) => {
    const changedStartTime =
      new Date(initData?.startTime ?? "").getHours() !== selectedStartTime?.getHours() ||
      new Date(initData?.startTime ?? "").getMinutes() !== selectedStartTime?.getMinutes();

    const changedEndTime =
      new Date(initData?.endTime ?? "").getHours() !== selectedEndTime?.getHours() ||
      new Date(initData?.endTime ?? "").getMinutes() !== selectedEndTime?.getMinutes();

    const changedMeetingRoom = initData?.meetingRoom.id !== selectedMeetingRoom?.id;
    const changeTitle = initData?.title !== title;
    const changeMemo = extractHtmlText(initData?.memo ?? "").text !== memo;
    const changePrivate = initData?.isPrivate !== isPrivate;

    if (
      isChangedAttendees ||
      changeTitle ||
      changeMemo ||
      changePrivate ||
      changedEndTime ||
      changedStartTime ||
      changedMeetingRoom
    ) {
      return true;
    }
    return false;
  },

  getInitStartTime: (startTime: string, reservationDate: Date) => {
    const rsvStartTime = new Date(startTime);
    const today = new Date();

    const hour = rsvStartTime
      ? rsvStartTime.getHours()
      : today.getMinutes() > 30
        ? today.getHours() + 1
        : today.getHours();
    const minutes = rsvStartTime
      ? rsvStartTime.getMinutes()
      : today.getMinutes() === 0
        ? 0
        : today.getMinutes() < 30
          ? 30
          : 0;

    const initDate = new Date(reservationDate);
    initDate.setHours(hour, minutes);
    return initDate;
  },

  getInitEndTime: (endTime: string, reservationDate: Date) => {
    const rsvEndTime = new Date(endTime);

    const hour = rsvEndTime.getMinutes() > 30 ? rsvEndTime.getHours() + 1 : rsvEndTime.getHours();
    const minutes = rsvEndTime.getMinutes() === 0 ? 0 : rsvEndTime.getMinutes() <= 30 ? 30 : 0;

    const initDate = reservationDate;
    initDate.setHours(hour, minutes);

    return initDate;
  },

  getInitInfo: (initReservationData: TReservation, defaultDate: Date) => {
    const transformedAttendee = (initReservationData?.reservationAttendees ?? []).map((item) => ({
      isVisitor: item.isVisitor,
      email: item.isVisitor ? item.visitorEmail : item.member.email,
      picture: item.isVisitor ? "" : item.member.image,
      id: item.isVisitor ? null : item.member.id,
      name: item.isVisitor ? "" : item.member.nickname,
    }));

    return {
      title: initReservationData.title ?? "",
      attendees: transformedAttendee,
      memo: extractHtmlText(initReservationData.memo ?? "").text,
      isPrivate: initReservationData.isPrivate,
      guestsCanModify: initReservationData.guestsCanModify,
      startTime: services.getInitStartTime(initReservationData.startTime, defaultDate),
      endTime: services.getInitEndTime(initReservationData.endTime, defaultDate),
    };
  },

  //*INFO: 이 일정 및 향후 일정 반복 예약 전 중복 체크
  checkRecurringReservationDuplicate: async (
    defaultDate: Date,
    selectedStartTime: Date,
    selectedEndTime: Date,
    rrule: string,
    meetingRoomId: string,
    editOption?: RepeatReservationConditionalOption,
    instanceId?: string
  ) => {
    const startRsvTime = new Date(defaultDate);
    if (selectedStartTime) startRsvTime.setHours(selectedStartTime?.getHours(), selectedStartTime.getMinutes(), 0, 0);

    const endRsvTime = new Date(defaultDate);
    if (selectedEndTime) endRsvTime.setHours(selectedEndTime.getHours(), selectedEndTime.getMinutes(), 0, 0);

    const res = await checkDuplicationRecurringReservation({
      rrule: rrule,
      meetingRoomId: meetingRoomId,
      rsvStartTime: startRsvTime.toISOString(),
      rsvEndTime: endRsvTime.toISOString(),
      editOption: editOption,
      instanceId: instanceId,
    });

    if (res.success && res.data) {
      return res.data;
    } else return null;
  },

  editReservation: async (
    defaultDate: Date,
    selectedStartTime: Date,
    selectedEndTime: Date,
    reservationId: string,
    title: string,
    memo: string,
    isPrivate: boolean,
    guestsCanModify: boolean,
    orgMeetingRoomId: string,
    googleEventId: string,
    attendees: TFormattedAttendee[],
    selectedMeetingRoom?: TMeetingRoom,
    rrule?: string,
    repeatOption?: RepeatReservationConditionalOption,
    exceptedAttendeeLimit?: boolean
  ) => {
    const startRsvTime = new Date(defaultDate);
    if (selectedStartTime) {
      startRsvTime.setHours(selectedStartTime.getHours(), selectedStartTime.getMinutes(), 0, 0);
    }

    const endRsvTime = new Date(defaultDate);
    if (selectedEndTime) {
      endRsvTime.setHours(selectedEndTime.getHours(), selectedEndTime.getMinutes(), 0, 0);
    }

    let editOption =
      repeatOption === RepeatReservationConditionalOption.ALL_SCHEDULES
        ? "ALL"
        : repeatOption === RepeatReservationConditionalOption.THIS_SCHEDULE
          ? "THIS"
          : repeatOption === RepeatReservationConditionalOption.THIS_SCHEDULE_AND_FUTURE_SCHEDULES
            ? "THIS_AND_FUTURE"
            : undefined;

    const data = {
      id: reservationId,
      title: title,
      memo: memo,
      isPrivate: isPrivate,
      guestsCanModify: guestsCanModify,
      rsvStartTime: startRsvTime.toISOString(),
      rsvEndTime: endRsvTime.toISOString(),
      beforeMeetingRoomId: orgMeetingRoomId,
      googleEventId: googleEventId,
      afterMeetingRoomId: selectedMeetingRoom?.id ?? "",
      reservationAttendees: attendees.map((attendee) => {
        return {
          isVisitor: attendee.isVisitor ?? "",
          visitorEmail: attendee.email ?? "",
          memberId: attendee.id ?? null,
        };
      }),
      rrule: rrule,
      editOption: editOption,
      exceptedAttendeeLimit: exceptedAttendeeLimit,
    };

    const res = await updateMeetingReservation(data);

    return res;
  },

  //* INFO: 현재 시간에 포함된 참석자 예약 제한 수
  getAttendeeLimit: (attendeeLimits: TAttendeeLimit[], startTime: Date, endTime: Date) => {
    const startTimeStr = startTime.toTimeString().split(" ")[0];
    const endTimeStr = endTime.toTimeString().split(" ")[0];

    // 주어진 시간 범위에 포함되는 attendeeLimits 필터링
    const includedLimits = attendeeLimits.filter((limit) => {
      return limit.startTime < endTimeStr && limit.endTime > startTimeStr;
    });

    // 포함된 시간이 없으면 기본값 0 반환
    if (includedLimits.length === 0) {
      return 0;
    }

    // 포함된 시간 중 최대 minAttendeeNum 찾기
    return Math.max(...includedLimits.map((limit) => limit.minAttendeeNum));
  },
};
